import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaSearch } from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function OrderDetailsList() {
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [loading, setLoading] = useState({});
  const [editingRemarksId, setEditingRemarksId] = useState(null);
  const [newRemark, setNewRemark] = useState("");
  const [orderStatus, setOrderStatus] = React.useState({});
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [editedAddress, setEditedAddress] = useState("");
  const [editedLandmark, setEditedLandmark] = useState("");
  const [editedCity, setEditedCity] = useState("");
  const [editedState, setEditedState] = useState("");
  const [editedPincode, setEditedPincode] = useState("");
  const [filterDate, setFilterDate] = useState("");

  const formatToMySQLDateTime = (isoString) => {
    const date = new Date(isoString);
    const pad = (n) => (n < 10 ? "0" + n : n);
    return (
      date.getFullYear() +
      "-" +
      pad(date.getMonth() + 1) +
      "-" +
      pad(date.getDate()) +
      " " +
      pad(date.getHours()) +
      ":" +
      pad(date.getMinutes()) +
      ":" +
      pad(date.getSeconds())
    );
  };

  const fetchOrders = async () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/orderdetails`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch orders");
        }

        const result = await response.json();
        console.log("API Response:", result);
        setOrders(result.data || []);
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    }
  };

  // Modified handleDispatch: It now takes the full order object and sends it to the /addshipment API.
  const handleDispatch = async (order) => {
    const token = localStorage.getItem("authToken");
    if (!token) return;

    setLoading((prev) => ({ ...prev, [order.OrderId]: true }));

    // Prepare payload using required fields
    const payload = {
      FirstName: order.FirstName,
      LastName: order.LastName,
      PhoneNumber: order.PhoneNumber,
      Email: order.Email,
      Address: order.Address,
      Landmark: order.Landmark, // Optional
      State: order.State,
      City: order.City,
      Pincode: order.Pincode,
      // Ensure Products is sent as a JSON string if it's not already a string.
      Products:
        typeof order.Products === "string"
          ? order.Products
          : JSON.stringify(order.Products),
      GrandTotal: order.GrandTotal,
      PaymentStatus: order.PaymentStatus,
      PaymentMode: order.PaymentMode,
      PaidAmount: order.PaidAmount,
      PaymentType: order.PaymentType,
      OrderNumber: order.OrderNumber,
      OrderDateTime: formatToMySQLDateTime(order.OrderDateTime),
    };

    console.log("Dispatch Payload:", payload);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/addshipment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to dispatch order");
      }

      // Step 2: Update remarks to "Dispatched"
      await fetch(
        `${process.env.REACT_APP_API_URL}/orderdetails/${order.OrderId}/remarks`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ newRemarks: "Dispatched" }),
        }
      );

      await fetchOrders();
      alert("Order dispatched successfully!");
    } catch (error) {
      console.error("Error dispatching order:", error);
      alert("Failed to dispatch order. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, [order.OrderId]: false }));
    }
  };

  const handleEditClick = (order) => {
    setEditingRemarksId(order.OrderId);
    setNewRemark(order.Remarks || "");
  };

  const handleCancelEdit = () => {
    setEditingRemarksId(null);
    setNewRemark("");
  };

  const handleSaveClick = async (orderId) => {
    const token = localStorage.getItem("authToken");
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/orderdetails/${orderId}/remarks`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ newRemarks: newRemark }),
        }
      );

      if (!response.ok) throw new Error("Failed to update remarks");

      await fetchOrders();
      setEditingRemarksId(null);
      setNewRemark("");
      alert("Remarks updated successfully");
    } catch (err) {
      alert("Error updating remarks");
      console.error(err);
    }
  };


  // New: Handle Cancel Order button click
  const handleCancelOrder = async (order) => {
    const token = localStorage.getItem("authToken");
    if (!token) return;

    if (!window.confirm(`Are you sure you want to cancel order ${order.OrderId}?`)) {
      return;
    }

    setLoading((prev) => ({ ...prev, [`cancel_${order.OrderId}`]: true }));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/orderdetails/${order.OrderId}/remarks`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ newRemarks: "Cancelled" }), // consistent key "Remarks"
        }
      );

      if (!response.ok) {
        throw new Error("Failed to cancel order");
      }

      await fetchOrders();
      alert(`Order ${order.OrderId} cancelled successfully!`);
      setOrderStatus((prev) => ({ ...prev, [order.OrderId]: "canceled" }));
    } catch (error) {
      console.error("Error cancelling order:", error);
      alert("Failed to cancel order. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, [`cancel_${order.OrderId}`]: false }));
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const filteredOrders = orders.filter((order) => {
    const lowerSearchTerm = searchTerm.toLowerCase();

    let products = [];
    try {
      products = JSON.parse(order.Products || "[]");
    } catch {
      products = [];
    }

    const matchesSearch =
      order.Email?.toLowerCase().includes(lowerSearchTerm) ||
      order.FirstName?.toLowerCase().includes(lowerSearchTerm) ||
      order.LastName?.toLowerCase().includes(lowerSearchTerm) ||
      order.PhoneNumber?.includes(searchTerm);

    const matchesProduct = products.some(product =>
      product.name.toLowerCase().includes(lowerSearchTerm)
    );

    if (!filterDate) {
      return matchesSearch || matchesProduct;
    }

    const orderDateFormatted = new Date(order.OrderDateTime).toISOString().slice(0, 10);
    const matchesDate = orderDateFormatted === filterDate;

    return (matchesSearch || matchesProduct) && matchesDate;
  });


  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Export to Excel function
  const exportToExcel = () => {
    const data = currentOrders.map((order) => {
      const products = Array.isArray(JSON.parse(order.Products || "[]"))
        ? JSON.parse(order.Products)
          .map(
            (p) =>
              `${p.name} (Size: ${p.size}, Qty: ${p.quantity}, Price: ₹${p.price})`
          )
          .join("; ")
        : "";

      const status = order.Remarks === "Dispatched"
        ? "Dispatched"
        : order.Remarks === "Cancelled"
          ? "Canceled"
          : "Pending";

      return {
        "Order ID": order.OrderId,
        "Customer Name": `${order.FirstName} ${order.LastName}`,
        Phone: order.PhoneNumber,
        Address: `${order.Address}${order.Landmark ? ", " + order.Landmark : ""}, ${order.City}, ${order.State} - ${order.Pincode}`,
        Products: products,
        "Total Amount (INR)": formatCurrency(order.GrandTotal),
        "Order Number": order.OrderNumber,
        "Payment Status": order.PaymentStatus,
        "Payment Mode": order.PaymentMode,
        "Payment Type": order.PaymentType,
        "Paid Amount (INR)": order.PaidAmount ? formatCurrency(order.PaidAmount) : "",
        "Order Date": formatDateTime(order.OrderDateTime),
        Remarks: order.Remarks || "",
        "Order Status": status,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const dataBlob = new Blob([excelBuffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(dataBlob, `orders_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleEditAddressClick = (order) => {
    setEditingAddressId(order.OrderId);
    setEditedAddress(order.UpdatedAddress || order.Address || "");
    setEditedLandmark(order.Landmark || "");
    setEditedCity(order.City || "");
    setEditedState(order.State || "");
    setEditedPincode(order.Pincode || "");
  };

  const handleCancelAddressEdit = () => {
    setEditingAddressId(null);
    setEditedAddress("");
    setEditedLandmark("");
    setEditedCity("");
    setEditedState("");
    setEditedPincode("");
  };

  const handleSaveAddress = async (orderId) => {
    const token = localStorage.getItem("authToken");
    if (!token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/orderdetails/${orderId}/address`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            updatedAddress: editedAddress,
            updatedLandmark: editedLandmark,
            updatedCity: editedCity,
            updatedState: editedState,
            updatedPincode: editedPincode,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to update address");

      await fetchOrders();
      handleCancelAddressEdit();
      alert("Address updated successfully");
    } catch (err) {
      alert("Error updating address");
      console.error(err);
    }
  };


  return (
    <div className="container" style={{ marginTop: "80px" }}>
      <h2 className="text-primary">Order Details</h2>

      <div className="row mb-4 align-items-center">
        {/* Left: Search Bar */}
        <div className="col-md-4 mb-2 mb-md-0">
          <div className="input-group">
            <span className="input-group-text bg-primary text-white">
              <FaSearch />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Center: Date Filter */}
        <div className="col-md-4 mb-2 mb-md-0 text-md-center">
          <div className="d-flex justify-content-md-center align-items-center gap-2">
            <label className="fw-bold mb-0">Filter by Date:</label>
            <input
              type="date"
              className="form-control"
              style={{ maxWidth: "170px" }}
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            />
          </div>
        </div>

        {/* Right: Export to Excel Button */}
        <div className="col-md-4 text-md-end">
          <button className="btn btn-success" onClick={exportToExcel}>
            Export to Excel
          </button>
        </div>
      </div>


      <div className="table-responsive">
        <table
          className="table table-bordered table-striped table-hover"
          style={{ width: "100%", margin: "auto" }}
        >
          <thead className="table-primary">
            <tr>
              <th style={{ width: "6%" }}>Order ID</th>
              <th style={{ width: "15%" }}>Customer Details</th>
              <th style={{ width: "15%" }}>Address</th>
              <th style={{ width: "10%" }}>Products</th>
              <th style={{ width: "8%" }}>Payment Details</th>
              <th style={{ width: "5%" }}>Order Date</th>
              <th style={{ width: "10%" }}>Remarks</th> {/* New column */}
              <th style={{ width: "10%" }}>Actions</th> {/* Dispatch & Cancel buttons */}
            </tr>
          </thead>
          <tbody>
            {currentOrders.map((order, index) => (
              <tr key={order.OrderId || index}>
                <td>{order.OrderId}</td>
                <td>
                  <div>
                    <strong>Name:</strong> {order.FirstName} {order.LastName}
                  </div>
                  <div>
                    <strong>Phone:</strong> {order.PhoneNumber}
                  </div>
                </td>
                <td>
                  {editingAddressId === order.OrderId ? (
                    <>
                      <textarea
                        value={editedAddress}
                        onChange={(e) => setEditedAddress(e.target.value)}
                        rows={2}
                        placeholder="Address"
                        style={{ width: "100%" }}
                      />

                      <input
                        className="form-control form-control-sm mt-1"
                        type="text"
                        value={editedLandmark}
                        onChange={(e) => setEditedLandmark(e.target.value)}
                        placeholder="Landmark"
                      />

                      <input
                        className="form-control form-control-sm mt-1"
                        type="text"
                        value={editedCity}
                        onChange={(e) => setEditedCity(e.target.value)}
                        placeholder="City"
                      />

                      <input
                        className="form-control form-control-sm mt-1"
                        type="text"
                        value={editedState}
                        onChange={(e) => setEditedState(e.target.value)}
                        placeholder="State"
                      />

                      <input
                        className="form-control form-control-sm mt-1"
                        type="text"
                        value={editedPincode}
                        onChange={(e) => setEditedPincode(e.target.value)}
                        placeholder="Pincode"
                      />

                      <div className="mt-2">
                        <button
                          className="btn btn-sm btn-success me-2"
                          onClick={() => handleSaveAddress(order.OrderId)}
                        >
                          Save
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={handleCancelAddressEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        {order.UpdatedAddress || order.Address}
                      </div>
                      {order.Landmark && (
                        <div>
                          <strong>Landmark:</strong> {order.Landmark}
                        </div>
                      )}
                      <div>
                        {order.City}, {order.State} - {order.Pincode}
                      </div>
                      <button
                        className="btn btn-sm btn-primary mt-1"
                        onClick={() => handleEditAddressClick(order)}
                      >
                        Edit
                      </button>
                    </>
                  )}
                </td>


                <td>
                  {Array.isArray(JSON.parse(order.Products || "[]")) &&
                    JSON.parse(order.Products).map((product, idx) => (
                      <div key={idx} className="mb-2">
                        <div>
                          <strong>{product.name}</strong>
                        </div>
                        <div>Size: {product.size}</div>
                        <div>Color: {product.color}</div>
                        <div>Quantity: {product.quantity}</div>
                        <div>Price: {formatCurrency(product.price)}</div>
                      </div>
                    ))}
                  <div className="mt-2 text-end">
                    <strong>Total: {formatCurrency(order.GrandTotal)}</strong>
                  </div>
                </td>
                <td>
                  <div>
                    <strong>Order Numer:</strong> {order.OrderNumber}
                  </div>
                  <div>
                    <strong>Status:</strong> {order.PaymentStatus}
                  </div>
                  <div>
                    <strong>Mode:</strong> {order.PaymentMode}
                  </div>
                  <div>
                    <strong>Type:</strong> {order.PaymentType}
                  </div>
                  {order.PaidAmount && (
                    <div>
                      <strong>Paid:</strong> {formatCurrency(order.PaidAmount)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  {formatDateTime(order.OrderDateTime)}
                </td>
                {/* Remarks Column */}
                <td>
                  {editingRemarksId === order.OrderId ? (
                    <>
                      <textarea
                        value={newRemark}
                        onChange={(e) => setNewRemark(e.target.value)}
                        rows={3}
                        style={{ width: "100%" }}
                      />
                      <div className="mt-1">
                        <button
                          className="btn btn-sm btn-success me-2"
                          onClick={() => handleSaveClick(order.OrderId)}
                        >
                          Save
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {order.Remarks || "-"}
                      </div>
                      {order.Remarks !== "Dispatched" && (
                        <button
                          className="btn btn-sm btn-primary mt-1"
                          onClick={() => handleEditClick(order)}
                        >
                          Edit
                        </button>
                      )}
                    </>
                  )}
                </td>

                {/* Action buttons: Edit, Dispatch, Cancel */}
                <td>
                  {/* Edit Remarks is already above in Remarks column, so here just Dispatch and Cancel */}
                  <button
                    className="btn btn-sm btn-warning me-2"
                    onClick={() => handleDispatch(order)}
                    disabled={
                      loading[order.OrderId] ||
                      orderStatus[order.OrderId] === "dispatched" ||
                      orderStatus[order.OrderId] === "canceled"
                    }
                  >
                    {loading[order.OrderId]
                      ? "Dispatching..."
                      : order.Remarks === "Dispatched"
                        ? "Dispatched"
                        : "Dispatch"}
                  </button>
                  {order.Remarks !== "Dispatched" && (
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleCancelOrder(order)}
                      disabled={
                        loading[`cancel_${order.OrderId}`] ||
                        orderStatus[order.OrderId] === "canceled" ||
                        orderStatus[order.OrderId] === "dispatched"
                      }
                    >
                      {loading[`cancel_${order.OrderId}`]
                        ? "Canceling..."
                        : orderStatus[order.OrderId] === "canceled"
                          ? "Canceled"
                          : "Cancel"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <nav className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage((curr) => Math.max(curr - 1, 1))}
              >
                Previous
              </button>
            </li>
            {[...Array(totalPages)].map((_, index) => (
              <li
                key={index}
                className={`page-item ${currentPage === index + 1 ? "active" : ""
                  }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${currentPage === totalPages ? "disabled" : ""
                }`}
            >
              <button
                className="page-link"
                onClick={() =>
                  setCurrentPage((curr) => Math.min(curr + 1, totalPages))
                }
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
}

export default OrderDetailsList;
